/**
 * Filtro dos eventos do Sentry, igual para a API e para as telas (AGENTS.md, regra 1).
 *
 * Lista do que pode passar (o resto é descartado): tipo do erro, pilha (arquivo, função,
 * linha, coluna), tags (rota, request id), ambiente, release, SDK e a URL sem query.
 * Não vai: mensagem da exceção, corpo, cabeçalhos, query, cookies, usuário, breadcrumbs,
 * contexto, variáveis locais, linhas de código.
 *
 * Os tipos são estruturais para o obs-sdk não depender do SDK do Sentry.
 */

type ExceptionValue = {
  stacktrace?: { frames?: readonly object[] | undefined } | undefined;
};

export type SentryLikeEvent = {
  exception?: { values?: readonly ExceptionValue[] | undefined } | undefined;
  request?: { url?: string | undefined } | undefined;
};

const KEEP_EVENT = [
  "type",
  "event_id",
  "timestamp",
  "platform",
  "level",
  "environment",
  "release",
  "dist",
  "tags",
  "sdk",
] as const;
const KEEP_FRAME = ["filename", "abs_path", "function", "module", "lineno", "colno", "in_app"];

function pick(obj: object, keys: readonly string[]): Record<string, unknown> {
  const src = obj as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const k of keys) if (src[k] !== undefined) out[k] = src[k];
  return out;
}

export function scrubSentryEvent<E extends SentryLikeEvent>(event: E): E {
  const out: Record<string, unknown> = pick(event, KEEP_EVENT);
  const values = event.exception?.values?.map((ex) => {
    const kept: Record<string, unknown> = pick(ex, ["type", "mechanism"]);
    const frames = ex.stacktrace?.frames?.map((f) => pick(f, KEEP_FRAME));
    if (frames) kept.stacktrace = { frames };
    return kept;
  });
  if (values) out.exception = { values };
  const url = event.request?.url?.split("?")[0]?.split("#")[0];
  if (url) out.request = { url };
  return out as E;
}

/**
 * `dataCollection` do Sentry 11 com tudo desligado: a primeira barreira (o SDK nem coleta).
 * O padrão do SDK é coletar usuário, cookies, cabeçalhos, corpo, query, variáveis locais e
 * linhas de código. O `scrubSentryEvent` acima é a segunda barreira.
 */
export const SENTRY_NO_DATA_COLLECTION = {
  userInfo: false,
  cookies: false,
  httpHeaders: false,
  httpBodies: [] as never[],
  urlQueryParams: false,
  graphQL: { document: false, variables: false },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  stackFrameVariables: false,
  frameContextLines: 0,
};
